import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, CheckCircle, XCircle, Clock } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, Table, Button, Badge, Modal, ConfirmDialog, toast
} from '../../components/common';
import { budgetApi } from '../../services/api';
import BudgetTransferForm from './BudgetTransferForm';
import './BudgetTransfer.css';

export default function BudgetTransferList() {
    const queryClient = useQueryClient();
    const [showForm, setShowForm] = useState(false);
    const [actionTransfer, setActionTransfer] = useState(null);
    const [actionType, setActionType] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['budgetTransfers'],
        queryFn: () => budgetApi.listTransfers?.() || Promise.resolve({ data: { data: [] } }),
        select: (res) => res.data?.data || []
    });

    const actionMutation = useMutation({
        mutationFn: ({ id, action }) => {
            if (action === 'approve') return budgetApi.approveTransfer(id);
            if (action === 'reject') return budgetApi.rejectTransfer(id);
            throw new Error('Unknown action');
        },
        onSuccess: (_, { action }) => {
            toast.success(`Transfer ${action}d successfully`);
            queryClient.invalidateQueries(['budgetTransfers']);
            setActionTransfer(null);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const columns = [
        {
            key: 'from',
            title: 'From Budget',
            render: (_, row) => (
                <div className="transfer-budget">
                    <span className="budget-code">{row.fromBudget?.budgetCode}</span>
                    <span className="budget-dept">{row.fromBudget?.department?.name}</span>
                </div>
            )
        },
        {
            key: 'arrow',
            title: '',
            width: 50,
            render: () => <ArrowRightLeft size={18} className="transfer-arrow" />
        },
        {
            key: 'to',
            title: 'To Budget',
            render: (_, row) => (
                <div className="transfer-budget">
                    <span className="budget-code">{row.toBudget?.budgetCode}</span>
                    <span className="budget-dept">{row.toBudget?.department?.name}</span>
                </div>
            )
        },
        {
            key: 'amount',
            title: 'Amount',
            dataIndex: 'amount',
            align: 'right',
            render: (amount) => <span className="transfer-amount">{formatCurrency(amount)}</span>
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            render: (status) => {
                const config = {
                    'pending': { variant: 'warning', icon: Clock },
                    'approved': { variant: 'success', icon: CheckCircle },
                    'rejected': { variant: 'danger', icon: XCircle }
                };
                const { variant } = config[status] || { variant: 'default' };
                return <Badge variant={variant}>{status}</Badge>;
            }
        },
        {
            key: 'actions',
            title: '',
            width: 180,
            align: 'right',
            render: (_, row) => (
                row.status === 'pending' && (
                    <div className="transfer-actions">
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => { setActionTransfer(row); setActionType('approve'); }}
                        >
                            Approve
                        </Button>
                        <Button
                            size="sm"
                            variant="danger"
                            onClick={() => { setActionTransfer(row); setActionType('reject'); }}
                        >
                            Reject
                        </Button>
                    </div>
                )
            )
        }
    ];

    return (
        <div className="budget-transfer-page">
            <PageHeader
                title="Budget Transfers"
                subtitle="Manage budget reallocation requests"
                breadcrumbs={[
                    { label: 'Budget', path: '/budgets' },
                    { label: 'Transfers' }
                ]}
                actions={
                    <Button icon={ArrowRightLeft} onClick={() => setShowForm(true)}>
                        Request Transfer
                    </Button>
                }
            />

            <Card>
                <Table
                    columns={columns}
                    data={data || []}
                    loading={isLoading}
                    emptyText="No budget transfers found"
                    rowKey="id"
                />
            </Card>

            <Modal
                isOpen={showForm}
                onClose={() => setShowForm(false)}
                title="Request Budget Transfer"
                size="md"
            >
                <BudgetTransferForm
                    onSuccess={() => {
                        setShowForm(false);
                        queryClient.invalidateQueries(['budgetTransfers']);
                    }}
                    onCancel={() => setShowForm(false)}
                />
            </Modal>

            <ConfirmDialog
                isOpen={!!actionTransfer}
                onClose={() => { setActionTransfer(null); setActionType(''); }}
                onConfirm={() => actionMutation.mutate({ id: actionTransfer?.id, action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Transfer`}
                message={`Are you sure you want to ${actionType} this budget transfer?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                variant={actionType === 'reject' ? 'danger' : 'primary'}
                loading={actionMutation.isPending}
            />
        </div>
    );
}
